
class TokenService {
  static TOKEN_KEY = 'btn_mi_sas';
  static TOKEN_EXPIRY_KEY = 'token_expiry';
  static REFRESH_TOKEN_KEY = 'refresh_token';
  
  static EXPIRY_BUFFER = 5 * 60 * 1000; 

  /**
   * Store token with expiration tracking
   * @param {string} token - JWT token
   * @param {string} refreshToken 
   * @param {number} expiresIn 
   */
  static setToken(token, refreshToken = null, expiresIn = null) {
    try {
      // Store token (base64 encoded for consistency with existing code)
      localStorage.setItem(this.TOKEN_KEY, JSON.stringify(btoa(token)));
      
      // Only set expiry if provided (for JWT tokens)
      if (expiresIn) {
        const expiryTime = Date.now() + (expiresIn * 1000);
        localStorage.setItem(this.TOKEN_EXPIRY_KEY, JSON.stringify(expiryTime));
        console.log('Token stored with expiry tracking');
      } else {
        // For Sanctum tokens, remove any existing expiry
        localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
        console.log('Sanctum token stored without expiry tracking');
      }
      
      if (refreshToken) {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, JSON.stringify(btoa(refreshToken)));
      }
      
    } catch (error) {
      console.error('Error storing token:', error);
      throw new Error('Failed to store authentication token');
    }
  }

  /**
   * Store Laravel Sanctum token (no expiry tracking)
   * @param {string} token - Sanctum token
   */
  static setSanctumToken(token) {
    this.setToken(token, null, null);
  }

  /**
   * Get token if valid and not expired
   * For Laravel Sanctum tokens, we don't enforce client-side expiry
   * @returns {string|null} - Valid token or null
   */
  static getToken() {
    try {
      const tokenData = localStorage.getItem(this.TOKEN_KEY);
      
      if (!tokenData) {
        return null;
      }

      const token = atob(JSON.parse(tokenData));
    
      const expiryData = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      
      if (expiryData) {
        const expiryTime = JSON.parse(expiryData);
        
        if (Date.now() >= (expiryTime - this.EXPIRY_BUFFER)) {
          console.warn('Token expired or about to expire');
          this.clearTokens();
          return null;
        }
      }

      
      return token;
    } catch (error) {
      console.error('Error retrieving token:', error);
      this.clearTokens(); 
      return null;
    }
  }

  /**
   * Get refresh token
   * @returns {string|null} - Refresh token or null
   */
  static getRefreshToken() {
    try {
      const refreshTokenData = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      return refreshTokenData ? atob(JSON.parse(refreshTokenData)) : null;
    } catch (error) {
      console.error('Error retrieving refresh token:', error);
      return null;
    }
  }

  /**
   * Check if token exists and is valid
   * @returns {boolean} - True if token is valid
   */
  static isTokenValid() {
    return this.getToken() !== null;
  }

  /**
   * Check if token is about to expire
   * For Sanctum tokens without expiry, this returns false
   * @returns {boolean} - True if token expires soon
   */
  static isTokenExpiringSoon() {
    try {
      const expiryData = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (!expiryData) return false;
      
      const expiryTime = JSON.parse(expiryData);
      const timeUntilExpiry = expiryTime - Date.now();
      
      // Return true if token expires within the buffer time
      return timeUntilExpiry <= this.EXPIRY_BUFFER && timeUntilExpiry > 0;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return false;
    }
  }

  /**
   * Get time until token expiry in milliseconds
   * @returns {number} - Milliseconds until expiry, or 0 if expired/invalid
   */
  static getTimeUntilExpiry() {
    try {
      const expiryData = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (!expiryData) return 0;
      
      const expiryTime = JSON.parse(expiryData);
      const timeUntilExpiry = expiryTime - Date.now();
      
      return Math.max(0, timeUntilExpiry);
    } catch (error) {
      console.error('Error calculating time until expiry:', error);
      return 0;
    }
  }

  /**
   * Clear all authentication tokens
   */
  static clearTokens() {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      console.log('All authentication tokens cleared');
    } catch (error) {
      console.error('Error clearing tokens:', error);
    }
  }

  /**
   * Decode JWT payload (for debugging/info purposes only)
   * @param {string} token - JWT token
   * @returns {object|null} - Decoded payload or null
   */
  static decodeTokenPayload(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      console.error('Error decoding token payload:', error);
      return null;
    }
  }

  /**
   * Get token info for debugging
   * @returns {object} - Token information
   */
  static getTokenInfo() {
    const token = this.getToken();
    if (!token) {
      return { valid: false, message: 'No valid token found' };
    }

    const payload = this.decodeTokenPayload(token);
    const timeUntilExpiry = this.getTimeUntilExpiry();
    
    return {
      valid: true,
      isExpiringSoon: this.isTokenExpiringSoon(),
      timeUntilExpiry: Math.floor(timeUntilExpiry / 1000),
      payload: payload ? {
        sub: payload.sub,
        exp: payload.exp,
        iat: payload.iat,
      } : null
    };
  }
}

export default TokenService;